<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  alert: { type: Object, required: true },
  filtersSummary: { type: String, default: '' },
  isWorking: { type: Boolean, default: false },
  isPreviewLoading: { type: Boolean, default: false },
  previewOpen: { type: Boolean, default: false },
  previewCount: { type: Number, default: 0 },
  previewItems: { type: Array, default: () => [] },
});

defineEmits([
  'openLead',
  'togglePreview',
  'toggleStatus',
  'delete',
  'openImovel',
]);

const { t, locale } = useI18n();
const formattedLocale = computed(() =>
  (locale.value || 'pt_BR').replace('_', '-')
);

const formatDateTime = value => {
  if (!value) return t('LEAD_ALERTS.CARD.NOT_SENT_YET');
  return new Date(value).toLocaleString(formattedLocale.value);
};

const formatCurrency = value => {
  if (value == null || value === '') return null;

  return new Intl.NumberFormat(formattedLocale.value, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const statusMeta = computed(() => {
  if (props.alert.status === 'paused') {
    return {
      label: t('LEAD_ALERTS.STATUS.PAUSED'),
      className: 'bg-amber-50 text-amber-700',
    };
  }

  if (props.alert.status === 'archived') {
    return {
      label: t('LEAD_ALERTS.STATUS.ARCHIVED'),
      className: 'bg-slate-100 text-slate-700',
    };
  }

  return {
    label: t('LEAD_ALERTS.STATUS.ACTIVE'),
    className: 'bg-emerald-50 text-emerald-700',
  };
});

const specialState = computed(() => {
  if (props.alert.kind === 'classifieds' || props.alert.is_classifieds) {
    return {
      key: 'classifieds',
      banner: t('LEAD_ALERTS.SPECIAL.CLASSIFIEDS_BANNER'),
      copy: t('LEAD_ALERTS.SPECIAL.CLASSIFIEDS_COPY'),
      tone: 'border-amber-300 bg-amber-50 text-amber-900',
    };
  }

  if (props.alert.no_time || props.alert.delivery_quota_reached) {
    return {
      key: 'no_time',
      banner: t('LEAD_ALERTS.SPECIAL.NO_TIME_BANNER'),
      copy: t('LEAD_ALERTS.SPECIAL.NO_TIME_COPY'),
      tone: 'border-rose-200 bg-rose-50 text-rose-900',
    };
  }

  return null;
});

const previewState = computed(() => {
  if (!props.previewOpen) return null;
  if (props.isPreviewLoading) return t('LEAD_ALERTS.CARD.MATCHES_LOADING');
  if (props.previewCount > 0) {
    return t('LEAD_ALERTS.CARD.MATCHES_COUNT', {
      count: props.previewCount,
    });
  }

  return t('LEAD_ALERTS.CARD.MATCHES_EMPTY');
});

const contactSummary = computed(() =>
  [props.alert.contact?.email, props.alert.contact?.phone_number]
    .filter(Boolean)
    .join(' · ')
);

const deliverySummary = computed(() => {
  const labels = {
    whatsapp: t('LEAD_ALERTS.DELIVERY.WHATSAPP'),
    sms: t('LEAD_ALERTS.DELIVERY.SMS'),
    email: t('LEAD_ALERTS.DELIVERY.EMAIL'),
    app: t('LEAD_ALERTS.DELIVERY.APP'),
  };

  return (props.alert.delivery_channels || [])
    .map(channel => labels[channel] || channel)
    .join(' · ');
});

const frequencyLabel = computed(() => {
  const labels = {
    always: t('LEAD_ALERTS.DELIVERY.ALWAYS'),
    daily: t('LEAD_ALERTS.DELIVERY.DAILY'),
    weekly: t('LEAD_ALERTS.DELIVERY.WEEKLY'),
  };

  return labels[props.alert.delivery_frequency] || labels.daily;
});
</script>

<template>
  <article
    class="rounded-lais border border-lais-border bg-white p-4 shadow-lais-card"
  >
    <div
      v-if="specialState"
      class="mb-3 flex flex-col gap-1 rounded-lais border px-3 py-2 text-[12px] leading-5"
      :class="[specialState.tone]"
    >
      <span class="text-[11px] font-semibold uppercase tracking-wide">{{
        specialState.banner
      }}</span>
      <span>{{ specialState.copy }}</span>
    </div>

    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2">
          <h3 class="text-[15px] font-semibold text-lais-text">
            {{ alert.title }}
          </h3>
          <span
            class="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide"
            :class="statusMeta.className"
          >
            {{ statusMeta.label }}
          </span>
        </div>

        <p class="mt-1 text-[12px] font-semibold text-lais-text-muted">
          {{ alert.contact?.name || t('LEAD_ALERTS.CARD.UNKNOWN_CLIENT') }}
        </p>
        <p v-if="contactSummary" class="mt-1 text-[12px] text-lais-text-muted">
          {{ contactSummary }}
        </p>
        <p class="mt-3 text-[13px] leading-6 text-lais-text-muted">
          {{ filtersSummary }}
        </p>

        <div class="mt-3 flex flex-wrap gap-2">
          <span
            class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-lais-primary"
          >
            {{ deliverySummary || t('LEAD_ALERTS.CARD.DEFAULT_CHANNEL') }}
          </span>
          <span
            class="rounded-full bg-[#f6f0ff] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#6b46c1]"
          >
            {{ frequencyLabel }}
          </span>
        </div>

        <p
          v-if="alert.delivery_note"
          class="mt-3 rounded-lais border border-lais-border bg-[#fffdf8] px-3 py-2 text-[12px] leading-5 text-lais-text-muted"
        >
          {{ alert.delivery_note }}
        </p>

        <div
          class="mt-3 grid gap-2 text-[12px] text-lais-text-muted sm:grid-cols-2"
        >
          <p>
            {{ t('LEAD_ALERTS.CARD.SENDS_COUNT_LABEL') }}
            <strong class="text-lais-text">{{
              alert.notifications_sent_count || 0
            }}</strong>
          </p>
          <p>
            {{ t('LEAD_ALERTS.CARD.LAST_SENT_LABEL') }}
            <strong class="text-lais-text">{{
              formatDateTime(alert.last_notified_at)
            }}</strong>
          </p>
        </div>
      </div>

      <div class="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
          @click="$emit('openLead')"
        >
          {{ t('LEAD_ALERTS.CARD.OPEN_LEAD') }}
        </button>
        <button
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-primary transition-all hover:bg-lais-primary-50"
          :disabled="isPreviewLoading"
          @click="$emit('togglePreview')"
        >
          {{
            previewOpen
              ? t('LEAD_ALERTS.CARD.HIDE_MATCHES')
              : t('LEAD_ALERTS.CARD.SHOW_MATCHES')
          }}
        </button>
        <button
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text transition-all hover:bg-lais-bg-page"
          :disabled="isWorking"
          @click="$emit('toggleStatus')"
        >
          {{
            alert.status === 'active'
              ? t('LEAD_ALERTS.CARD.PAUSE')
              : t('LEAD_ALERTS.CARD.ACTIVATE')
          }}
        </button>
        <button
          type="button"
          class="rounded-lais border border-[#f0d4d4] px-3 py-2 text-[12px] font-semibold text-[#b42318] transition-all hover:bg-[#fff5f5]"
          :disabled="isWorking"
          @click="$emit('delete')"
        >
          {{ t('LEAD_ALERTS.CARD.DELETE') }}
        </button>
      </div>
    </div>

    <div
      v-if="previewOpen"
      class="mt-4 rounded-lais border border-lais-border bg-lais-bg-page p-4"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="text-[12px] font-semibold text-lais-text">
          {{ t('LEAD_ALERTS.CARD.PREVIEW_TITLE') }}
        </p>
        <span
          class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[10px] font-semibold text-lais-primary"
        >
          {{ t('LEAD_ALERTS.CARD.MATCHES_BADGE', { count: previewCount }) }}
        </span>
      </div>

      <p class="mt-2 text-[12px] text-lais-text-muted">
        {{ previewState }}
      </p>

      <div v-if="previewItems.length" class="mt-3 grid gap-3">
        <article
          v-for="item in previewItems"
          :key="item.id"
          class="rounded-lais border border-lais-border bg-white px-3 py-3"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <p class="text-[13px] font-semibold text-lais-text">
                {{
                  item.titulo ||
                  t('LEAD_ALERTS.CARD.PROPERTY_TITLE', { id: item.id })
                }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                {{
                  [item.bairro, item.cidade, item.tipo]
                    .filter(Boolean)
                    .join(' · ')
                }}
              </p>
              <p
                v-if="formatCurrency(item.valor_aluguel || item.valor_venda)"
                class="mt-2 text-[12px] font-semibold text-lais-text"
              >
                {{ formatCurrency(item.valor_aluguel || item.valor_venda) }}
              </p>
            </div>

            <button
              type="button"
              class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
              @click="$emit('openImovel', item)"
            >
              {{ t('LEAD_ALERTS.CARD.OPEN_PROPERTY') }}
            </button>
          </div>
        </article>
      </div>
    </div>
  </article>
</template>
